import { Controller } from "@hotwired/stimulus";
import { EditorModule } from "../datagrid_modules/editor.js";
import { ScrollPositionModule } from "../datagrid_modules/scroll_position.js";
import { Modal } from "bootstrap";
import { TabulatorFull as Tabulator } from "tabulator-tables";
import { Turbo } from "@hotwired/turbo-rails";
import rison from "rison";

Tabulator.extendModule("format", "formatters", {
  record_id: function (cell, formatterParams) {
    return this.emptyToSpace(
      this.sanitizeHTML(formatterParams.id_prefix + String(cell.getValue()).padStart(formatterParams.id_length, "0")),
    );
  },
  has_many_list: function (cell, formatterParams) {
    const value = cell.getValue();
    if (!value || !value.length) {
      return "";
    }

    if (value.length <= 3) {
      return this.emptyToSpace(
        this.sanitizeHTML(
          value
            .map((id) => formatterParams[id])
            .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
            .join(", "),
        ),
      );
    }

    const count = value.length;
    return `${count} associated ${count === 1 ? "record" : "records"}`;
  },
  has_many_read_only: function (cell, formatterParams) {
    if (typeof cell.getValue() === "undefined") {
      return "";
    }
    return this.emptyToSpace(this.sanitizeHTML(cell.getValue().join(", ")));
  },
  roles: function (cell, formatterParams) {
    const rolesContainer = document.createElement("span");
    rolesContainer.className = "d-flex flex-row";
    cell.getValue().forEach((id) => {
      const roleElement = document.createElement("div");
      const role = formatterParams[id] || id;
      const roleClass = role
        .replace(/([a-z])([A-Z])/g, "$1-$2")
        .replace(/[\s_]+/g, "-")
        .toLowerCase();

      roleElement.className = "badge rounded-pill me-2 role-" + roleClass;
      roleElement.innerText = role;
      rolesContainer.insertAdjacentElement("beforeend", roleElement);
    });
    return rolesContainer;
  },
  row_actions: function (cell, formatterParams, onRendered) {
    const actionContainer = document.createElement("div");
    actionContainer.className = "d-flex flex-row align-items-center justify-content-center ";

    if (formatterParams.allowBundle === true) {
      const bundleIcon = document.createElement("i");
      bundleIcon.className = "bundle-btn me-2";
      bundleIcon.setAttribute("data-action", "click->datagrid#rowBundleHandler");
      actionContainer.insertAdjacentElement("beforeend", bundleIcon);
    }

    if (formatterParams.allowShow === true) {
      const showIcon = document.createElement("i");
      showIcon.className = "show-btn me-2";
      showIcon.setAttribute("data-action", "click->datagrid#rowShowHandler");
      actionContainer.insertAdjacentElement("beforeend", showIcon);
    }

    if (formatterParams.allowEdit === true) {
      const editIcon = document.createElement("i");
      editIcon.className = "edit-btn me-2";
      editIcon.setAttribute("data-action", "click->datagrid#rowEditHandler");
      actionContainer.insertAdjacentElement("beforeend", editIcon);
    }

    if (formatterParams.allowDestroySingle === true) {
      const deleteIcon = document.createElement("i");
      deleteIcon.className = "delete-btn";
      deleteIcon.setAttribute("data-action", "click->datagrid#rowDeleteHandler");
      actionContainer.insertAdjacentElement("beforeend", deleteIcon);
    }

    return actionContainer;
  },
  user_status: function (cell) {
    const val = cell.getValue();
    const span = document.createElement("span");
    span.className = "status-" + val;
    span.textContent = val ? "Suspended" : "Active";
    return span;
  },
});
Tabulator.registerModule([EditorModule, ScrollPositionModule]);

export default class extends Controller {
  static targets = [
    "columnVisibilityElement",
    "bundleRowButton",
    "data",
    "datagrid",
    "deleteRowButton",
    "externalPageCounter",
    "searchInput",
    "pageCounterString",
    "rowStats",
  ];

  static values = {
    newUrl: String,
    columns: Array,
    confirmDeleteMessageSingle: String,
    confirmDeleteMessagePlural: String,
    bundleUrl: String,
    createUrl: String,
    dateFilter: String,
    datagrid: Object,
    datasourceUrl: String,
    deleteMultipleUrl: String,
    deleteSingleUrl: String,
    editUrl: String,
    id: String,
    persistenceId: String,
    placeholderHtml: { type: String, default: "There are no records." },
    recordLabel: { type: String, default: "Record" },
    recordLabelPlural: { type: String, default: "Records" },
    rootParam: String,
    showUrl: String,
    updateUrl: String,
    url: String,
    allowInCellEditing: { type: Boolean, default: true },
    allowBundle: Boolean,
    allowShow: Boolean,
    allowCreate: Boolean,
    allowEdit: Boolean,
    allowDestroySingle: Boolean,
    allowDestroyMultiple: Boolean,
  };

  static outlets = ["datagrid--status-feedback", "datagrid--query-builder"];

  connect() {
    this.boundShowDatagridAlertHandler = this.showDatagridAlertHandler.bind(this);
    this.boundPaginationCounterHandler = this.paginationCounterHandler.bind(this);

    if (this.allowCreateValue === true) {
      this.boundCreateHandler = this.createHandler.bind(this);
    }

    if (this.allowEditValue === true) {
      this.boundUpdateRowHandler = this.updateRowHandler.bind(this);
      this.boundCellEditedHandler = this.cellEditedHandler.bind(this);
    }

    if (this.allowDestroySingleValue === true || this.allowDestroyMultipleValue === true) {
      this.boundDeleteRowsHandler = this.deleteRowsHandler.bind(this);
    }

    if (this.allowDestroyMultipleValue === true || this.allowBundleValue === true) {
      this.boundRowSelectionChangedHandler = this.rowSelectionChangedHandler.bind(this);
    }

    // Freeze the first column if it is the ID column
    if (this.columnsValue.length > 0 && this.columnsValue[0].field === "nu_id_number") {
      this.columnsValue = [{ ...this.columnsValue[0], frozen: true }, ...this.columnsValue.slice(1)];
    }

    // Add right-most Actions column
    if (
      !this.columnsValue.some((value) => {
        return value.title === "Actions";
      })
    ) {
      this.columnsValue = this.columnsValue.concat({
        title: "Actions",
        headerSort: false,
        headerHozAlign: "center",
        hozAlign: "center",
        formatter: "row_actions",
        frozen: "true",
        width: 120,
        minWidth: 120,
        maxWidth: 120,
        formatterParams: {
          allowBundle: this.allowBundleValue,
          allowShow: this.allowShowValue,
          allowCreate: this.allowCreateValue,
          allowEdit: this.allowEditValue,
          allowDestroySingle: this.allowDestroySingleValue,
          allowDestroyMultiple: this.allowDestroyMultipleValue,
        },
      });
    }

    // Only show row checkboxes if bulk delete or bundle access is allowed
    this.rowHeaderConfigs =
      this.allowDestroyMultipleValue || this.allowBundleValue
        ? {
            headerSort: false,
            resizable: false,
            minWidth: 50,
            maxWidth: 50,
            frozen: true,
            headerHozAlign: "center",
            hozAlign: "center",
            formatter: "rowSelection",
            titleFormatter: "rowSelection",
            cellClick: function (e, cell) {
              cell.getRow().toggleSelect();
            },
          }
        : null;

    this.columns = this.allowInCellEditingValue
      ? this.columnsValue
      : this.columnsValue.map((col) => {
          const { editor, editable, editorParams, ...rest } = col;
          return { ...rest };
        });

    this.datagrid = new Tabulator(this.datagridTarget, {
      index: "id",
      popupContainer: true,
      layout: "fitColumns",
      resizableColumnFit: true,
      ajaxURL: this.datasourceUrlValue,
      ajaxParams: () => {
        // Check if advanced search using datagrid exists
        if (this.hasDatagridQueryBuilderOutlet && this.datagridQueryBuilderOutlet.isQueryBuilderEnabled()) {
          const ni_data_query = this.datagridQueryBuilderOutlet.retrieveQuery();
          return { ni_data_query };
        } else {
          // Get the value of the search input field
          return { search_query: this.searchInputTarget.value };
        }
      },
      dataReceiveParams: {
        last_page: "total_pages",
        last_row: "total_rows",
      },
      sortMode: "remote",
      filterMode: "remote",
      pagination: true,
      paginationMode: "remote",
      paginationSize: 50,
      paginationSizeSelector: true,
      paginationCounter: this.boundPaginationCounterHandler,
      persistence: {
        sort: true,
        filter: false,
        group: false,
        page: false,
        columns: true,
      },
      persistenceID: this.persistenceIdValue,
      placeholder: () => {
        // Set placeholder based on if there are currently any filters
        if (
          (this.hasDatagridQueryBuilderOutlet && this.datagridQueryBuilderOutlet.isQueryBuilderEnabled()) ||
          this.searchInputTarget.value !== ""
        ) {
          return "<span>No Matching Data</span>";
        } else {
          return this.placeholderHtmlValue;
        }
      },
      movableColumns: true,
      columnDefaults: {
        minWidth: 100,
        cellEdited: this.allowEditValue === true ? this.boundCellEditedHandler : null,
      },
      columns: this.columns,
      rowHeader: this.rowHeaderConfigs,
      headerSortElement: "",
    });

    this.datagrid.on("tableBuilt", () => {
      // Update column visibility depending on persisted options
      if (this.hasColumnVisibilityElementTarget) {
        const visible_columns = this.datagrid
          .getColumnDefinitions()
          .filter(function (col) {
            return col.visible;
          })
          .map(function (col) {
            return col.field;
          });

        this.dispatch("update.column.visibility", { detail: { visible_columns } });
      }
    });

    this.datagrid.on("dataLoadError", (error) => {
      if (error && error.json) {
        error.json().then((json) => {
          this.datagridStatusFeedbackOutlet.hide(json.error.join(", "), true);
        });
      }
    });

    if (this.allowCreateValue === true) {
      document.addEventListener("create-datagrid-row", this.boundCreateHandler);
    }

    if (this.allowEditValue === true) {
      document.addEventListener("update-datagrid-row", this.boundUpdateRowHandler);
    }

    if (this.allowDestroySingleValue === true || this.allowDestroyMultipleValue === true) {
      document.addEventListener("delete-datagrid-rows", this.boundDeleteRowsHandler);
    }

    if (this.allowDestroyMultipleValue === true || this.allowBundleValue === true) {
      if (this.hasRowStatsTarget || this.hasDeleteRowButtonTarget || this.hasBundleRowButtonTarget) {
        this.datagrid.on("rowSelectionChanged", this.boundRowSelectionChangedHandler);
      }
    }

    document.addEventListener("show-datagrid-alert", this.boundShowDatagridAlertHandler);

    this.headerFilters = this.datagrid.getColumns().map((obj) => ({ field: obj.name, headerFilter: obj.headerFilter }));
  }

  disconnect() {
    if (this.allowCreateValue === true) {
      document.removeEventListener("create-datagrid-row", this.boundCreateHandler);
    }

    if (this.allowEditValue === true) {
      document.removeEventListener("update-datagrid-row", this.boundUpdateRowHandler);
    }

    if (this.allowDestroySingleValue === true || this.allowDestroyMultipleValue === true) {
      document.removeEventListener("delete-datagrid-rows", this.boundDeleteRowsHandler);
    }

    document.removeEventListener("show-datagrid-alert", this.boundShowDatagridAlertHandler);
    this.datagrid.destroy();
  }

  // Create record handler
  createHandler() {
    const { data } = event.detail;

    this.datagrid.addData([data]);
    this.updatePageCounter();

    if (this.hasDatagridStatusFeedbackOutlet) {
      this.datagridStatusFeedbackOutlet.showAndHideWithoutSpinner(`${this.recordLabelValue} created.`);
    }
  }

  // Update handler after edit modal submission
  updateRowHandler() {
    const { data } = event.detail;

    this.datagrid.updateData([data]);

    if (this.hasDatagridStatusFeedbackOutlet) {
      this.datagridStatusFeedbackOutlet.showAndHideWithoutSpinner(`${this.recordLabelValue} saved.`);
    }
  }

  // Handler for both individual or multiple rows deletion
  deleteRowsHandler() {
    const { ids } = event.detail;

    this.datagrid.deleteRow(ids);
    this.updatePageCounter();

    if (this.hasDatagridStatusFeedbackOutlet) {
      this.datagridStatusFeedbackOutlet.hide(
        `${ids.length} ${ids.length > 1 ? this.recordLabelPluralValue.toLowerCase() : this.recordLabelValue.toLowerCase()} deleted.`,
      );
    }
  }

  updatePageCounter() {
    const pageSize = this.datagrid.getPageSize();
    const currentPage = this.datagrid.getPage();
    const currentRow = (currentPage - 1) * pageSize + 1;
    const totalRows = this.datagrid.getDataCount("active");

    const pageCounterString = this.paginationCounterHandler(pageSize, currentRow, currentPage, totalRows);

    // Manually update page counter
    const internalCounter = this.datagrid.element.querySelector(".tabulator-page-counter");
    if (internalCounter) {
      internalCounter.innerHTML = pageCounterString;
    }
  }

  showDatagridAlertHandler() {
    const { message } = event.detail;

    this.datagrid.alert(message);
    this.datagrid.element.getElementsByClassName("tabulator-alert")[0].dataset.action = "click->datagrid#closeAlert";

    if (this.hasDatagridStatusFeedbackOutlet) {
      this.datagridStatusFeedbackOutlet.clear();
    }

    // Close any modals
    const modalElements = document.getElementsByClassName("modal");
    modalElements.forEach((modal) => {
      const modalInstance = Modal.getInstance(modal);
      if (modalInstance) {
        modalInstance.hide();
      }
    });
  }

  closeAlert() {
    this.datagrid.clearAlert();
  }

  searchQueryChangeHandler(_) {
    clearTimeout(this.debounceTimer);

    // Set a new timer
    this.debounceTimer = setTimeout(() => {
      this.datagrid.setData();
    }, 500);
  }

  queryToURL(event) {
    event.preventDefault();
    if (this.datagridQueryBuilderOutlet && this.datagridQueryBuilderOutlet.isQueryBuilderEnabled()) {
      const encodedRisonQuery = rison.encode(this.datagridQueryBuilderOutlet.retrieveQuery());

      // Build URL string manually
      // URLSearchParams.append() would percent-encode the '(' and ':' in our rison format
      const base = window.location.href.split("?")[0];

      // Support existing params
      const otherParams = window.location.search
        .replace(/^\?/, "")
        .split("&")
        .filter((p) => p && !p.startsWith("nidq="));
      otherParams.push(`nidq=${encodedRisonQuery}`);
      this.showCopiedToClipboardText(event);
      navigator.clipboard.writeText(`${base}?${otherParams.join("&")}`);
    }
  }

  showCopiedToClipboardText(event) {
    const btn = event.currentTarget;
    if (btn.dataset.copiedText !== btn.textContent) {
      const originalText = btn.textContent;
      btn.textContent = "Copied!";
      setTimeout(() => {
        btn.textContent = originalText;
      }, 2000);
    }
  }

  rowSelectionChangedHandler(data, rows) {
    if (data.length === 0) {
      if (this.hasRowStatsTarget) {
        this.rowStatsTarget.innerHTML = "No rows selected";
      }
      if (this.hasDeleteRowButtonTarget) {
        this.deleteRowButtonTarget.disabled = true;
      }
      if (this.hasBundleRowButtonTarget) {
        this.bundleRowButtonTarget.disabled = true;
      }
    } else {
      if (this.hasRowStatsTarget) {
        this.rowStatsTarget.innerHTML = `${data.length} row${data.length > 1 ? "s" : ""} selected`;
      }
      if (this.hasDeleteRowButtonTarget) {
        this.deleteRowButtonTarget.disabled = false;
      }
      if (this.hasBundleRowButtonTarget) {
        this.bundleRowButtonTarget.disabled = false;
      }
    }

    // Update pagination message
    if (data.length === 0) {
      if (this.hasPageCounterStringTarget) {
        this.pageCounterStringTargets.forEach((target) => {
          target.innerHTML = this.savedPageCounterString;
        });
      }
    } else {
      const selectedString = `${data.length} ${data.length > 1 ? "rows" : "row"} selected of`;
      this.savedPageCounterString ||= this.pageCounterStringTarget.innerHTML;
      if (this.hasPageCounterStringTarget) {
        this.pageCounterStringTargets.forEach((target) => {
          target.innerHTML = selectedString;
        });
      }
    }
  }

  cellEditedHandler(cell) {
    const data = {};
    data[cell.getColumn().getField()] = cell.getValue();
    const params = {};
    params[this.rootParamValue] = data;

    if (this.hasDatagridStatusFeedbackOutlet) {
      this.datagridStatusFeedbackOutlet.show("Saving...");
    }

    fetch(this.updateUrlValue.replace(":id", cell.getData().id), {
      method: "PATCH",
      headers: this.defaultFetchHeaders(),
      body: JSON.stringify(params),
    })
      .then((response) => {
        if (this.hasDatagridStatusFeedbackOutlet) {
          if (response.ok) {
            this.datagridStatusFeedbackOutlet.hide("Saved.");
          } else {
            this.datagridStatusFeedbackOutlet.hide("Error saving.", true);
          }
        }
        return response;
      })
      .then(this.handleResponse)
      .then(this.handleTurboStreamMessage)
      .catch(this.handleResponseError);
  }

  paginationCounterHandler(pageSize, currentRow, currentPage, totalRows, totalPages) {
    let pageCounterString;

    if (totalPages <= 1) {
      // Single page: "Showing 25 records."
      pageCounterString = `<span data-datagrid-target='pageCounterString'>Showing ${totalRows.toLocaleString()} records.</span>`;
    } else {
      // Multiple pages: "Showing 1-24 of 56 records."
      pageCounterString = `<span data-datagrid-target='pageCounterString'>Showing ${currentRow}-${Math.min(currentRow + pageSize - 1, totalRows)} of ${totalRows.toLocaleString()} records.</span>`;
    }

    if (this.hasExternalPageCounterTarget) {
      this.externalPageCounterTarget.innerHTML = pageCounterString;
    }

    // Hide pagination footer when all results fit on one page
    this.datagridTarget.classList.toggle("hide-pagination", totalPages <= 1);

    return pageCounterString;
  }

  addHandler() {
    fetch(this.newUrlValue, {
      method: "GET",
      headers: this.defaultFetchHeaders(),
    })
      .then(this.handleResponse)
      .then(this.handleTurboStreamMessage)
      .catch(this.handleResponseError);
  }

  selectedRowsDeleteHandler() {
    const data = this.datagrid.getSelectedData().map((row) => row.id);

    if (data.length === 0) {
      console.error("Unable to find row data.");
      return;
    }

    if (confirm(data.length > 1 ? this.confirmDeleteMessagePluralValue : this.confirmDeleteMessageSingleValue)) {
      if (this.hasDatagridStatusFeedbackOutlet) {
        this.datagridStatusFeedbackOutlet.show(`Deleting ${this.recordLabelValue.toLowerCase()}`);
      }

      this.requestDeleteMultipleRecord(data);
    }
  }

  rowBundleHandler(event) {
    event.preventDefault();

    const row = event.target.closest(".tabulator-row");
    const rowData = row ? this.datagrid.getRow(row).getData() : null;

    if (!rowData) {
      console.error("Unable to find row data.");
      return;
    }

    this.requestBundleOrganize([rowData.id]);
  }

  selectedRowsBundleHandler() {
    const ids = this.datagrid.getSelectedData().map((row) => row.id);

    if (ids.length === 0) {
      console.error("Unable to find row data.");
      return;
    }

    this.requestBundleOrganize(ids);
  }

  requestBundleOrganize(ids) {
    const params = new URLSearchParams();
    ids.forEach((id) => {
      params.append("records[][type]", this.rootParamValue);
      params.append("records[][id]", id);
    });
    fetch(`${this.bundleUrlValue}?${params}`, {
      method: "GET",
      headers: this.defaultFetchHeaders(),
    })
      .then(this.handleResponse)
      .then(this.handleTurboStreamMessage)
      .catch(this.handleResponseError);
  }

  rowShowHandler(event) {
    event.preventDefault();

    const row = event.target.closest(".tabulator-row");
    const rowData = row ? this.datagrid.getRow(row).getData() : null;

    if (!rowData) {
      console.error("Unable to find row data.");
      return;
    }

    fetch(this.showUrlValue.replace(":id", rowData.id), {
      method: "GET",
      headers: this.defaultFetchHeaders(),
    })
      .then(this.handleResponse)
      .then(this.handleTurboStreamMessage)
      .catch(this.handleResponseError);
  }

  rowEditHandler(event) {
    event.preventDefault();

    const row = event.target.closest(".tabulator-row");
    const rowData = row ? this.datagrid.getRow(row).getData() : null;

    if (!rowData) {
      console.error("Unable to find row data.");
      return;
    }

    fetch(this.editUrlValue.replace(":id", rowData.id), {
      method: "GET",
      headers: this.defaultFetchHeaders(),
    })
      .then(this.handleResponse)
      .then(this.handleTurboStreamMessage)
      .catch(this.handleResponseError);
  }

  rowDeleteHandler(event) {
    if (confirm(this.confirmDeleteMessageSingleValue)) {
      event.preventDefault();

      const row = event.target.closest(".tabulator-row"); // Locate the row
      const rowData = row ? this.datagrid.getRow(row).getData() : null;

      if (!rowData) {
        console.error("Unable to find row data.");
        return;
      }

      if (this.hasDatagridStatusFeedbackOutlet) {
        this.datagridStatusFeedbackOutlet.show(`Deleting ${this.recordLabelValue.toLowerCase()}`);
      }

      this.requestDeleteSingleRecord(rowData.id);
    }
  }

  requestDeleteSingleRecord(id) {
    fetch(this.deleteSingleUrlValue.replace(":id", id), {
      method: "DELETE",
      body: JSON.stringify({ request_from_datagrid: true }),
      headers: this.defaultFetchHeaders(),
    })
      .then(this.handleResponse)
      .then(this.handleTurboStreamMessage)
      .catch(this.handleResponseError);
  }

  requestDeleteMultipleRecord(ids) {
    fetch(this.deleteMultipleUrlValue, {
      method: "DELETE",
      body: JSON.stringify({ ids, request_from_datagrid: true }),
      headers: this.defaultFetchHeaders(),
    })
      .then(this.handleResponse)
      .then(this.handleTurboStreamMessage)
      .catch(this.handleResponseError);
  }

  handleResponse(response) {
    if (response.ok || response.status === 422) {
      return response.text();
    } else {
      console.error("Failed to receive a Turbo Stream response.");
      return Promise.reject(response);
    }
  }

  handleTurboStreamMessage(turboStream) {
    Turbo.renderStreamMessage(turboStream);
  }

  handleResponseError(error) {
    console.error("AJAX Error:", error);
  }

  defaultFetchHeaders() {
    return {
      "Content-Type": "application/json",
      Accept: "text/vnd.turbo-stream.html",
      "X-CSRF-Token": document.head.querySelector("meta[name='csrf-token']")?.content,
    };
  }

  updateColumnVisibility(field, isVisible) {
    if (isVisible) {
      this.datagrid.showColumn(field);
    } else {
      this.datagrid.hideColumn(field);
    }
  }

  clearFiltersHandler() {
    this.searchInputTarget.value = "";
    this.datagrid.modules.sort.clear(); // Clear sort without triggering another remote request
    this.datagrid.clearFilter(true);
  }
}
